import * as RTK from '@reduxjs/toolkit';
import { LayoutT } from '../data/shareLayout';
import * as LF from '../data/layout/furniture';
import * as LMF from '../data/layout/modularFurniture';

/** A loading state to help with loading either a layout or a single item of furniture
 *
 * If startSpace is called with a product set or an existing layout, then this will be used to
 * help load the entire layout.
 *
 * If startSpace is called with a single product ID, then this will be used to help load it.
 */

export type State = {
    kind: 'layout' | 'product' | null;
    /** when kind is single, then this is the product id of the product to load
     * Otherwise this is null */
    singleProductIdToLoad: string | null;
    totalModularFurnitureToLoad: Array<LMF.LayoutModularFurnitureT>;
    totalFurnitureToLoad: Array<LF.LayoutFurnitureT>;
    /** an identifier of the currently loaded or loading layout or product. This starts off
     *  undefined and is set when we begin loading a requested layout/product. It's purpose
     *  is to prevent us from initiating loading of the same layout/product multiple times.
     *
     *  When set, this is typically either the product set sku if the layout is from a
     *  product set or a layout id if it's from a saved layout, or a product sku if we're
     *  loading a single product, but in practice it should not matter as
     *  long as we can compare it to another item we are considering loading.
     */
    currentLoadingId: string | null;
    /** furniture for which loading has not been initiated, purpose similar to currentLayout
     *  layout above but for furniture.
     */
    furnitureLeftToLoad: Array<LF.LayoutFurnitureT>;
    /** sofas for which loading has not been initiated, purpose similar to currentLayout
     *  above but for sofas.
     */
    sofasLeftToLoad: Array<LMF.LayoutModularFurnitureT>;
    currentlyLoading: boolean;
};

const initialState: State = {
    kind: null,
    singleProductIdToLoad: null,
    furnitureLeftToLoad: [],
    sofasLeftToLoad: [],
    currentLoadingId: null,
    totalFurnitureToLoad: [],
    totalModularFurnitureToLoad: [],
    currentlyLoading: false,
};

export const slice = RTK.createSlice({
    name: 'loading',
    initialState,
    reducers: {
        startLoadingSingleProduct(state, action: RTK.PayloadAction<string>) {
            state.kind = 'product';
            state.singleProductIdToLoad = action.payload;
            state.currentlyLoading = true;
            state.currentLoadingId = action.payload;
        },
        startLoadingLayout(state, action: RTK.PayloadAction<LayoutT>) {
            state.kind = 'layout';
            state.totalFurnitureToLoad = action.payload.furniture;
            state.totalModularFurnitureToLoad = action.payload.modularFurniture;
            state.furnitureLeftToLoad = action.payload.furniture;
            state.sofasLeftToLoad = action.payload.modularFurniture;
            state.currentlyLoading = true;
            state.currentLoadingId = action.payload.id;
        },
        completeLoading(state, _: RTK.Action) {
            state.currentlyLoading = false;
        },
        clearLoading(_, __: RTK.Action) {
            return { ...initialState };
        },
        addingFurniture(state, action: RTK.PayloadAction<LF.LayoutFurnitureT>) {
            state.furnitureLeftToLoad = state.furnitureLeftToLoad.filter(
                f => !LF.eq(f, action.payload),
            );
        },
        addingSofa(state, action: RTK.PayloadAction<LMF.LayoutModularFurnitureT>) {
            state.sofasLeftToLoad = state.sofasLeftToLoad.filter(s => !LMF.eq(s, action.payload));
        },
        addingSofaFailed(state, action: RTK.PayloadAction<LMF.LayoutModularFurnitureT>) {
            state.sofasLeftToLoad.push(action.payload);
        },
    },
});

export const {
    addingFurniture,
    addingSofa,
    addingSofaFailed,
    clearLoading,
    startLoadingLayout,
    completeLoading,
    startLoadingSingleProduct,
} = slice.actions;

export const reducer = slice.reducer;
